// The year's attendee directories (fullstack claude/ATTENDEES-SPEC.md): {masterclass, workshop},
// only those the signed-in viewer may see (the server decides), and each attendee's recent
// activity, refreshed about every 2 minutes while the page is visible. One state for the page's
// sections and the contact dialog.
import {computed, onMounted, onUnmounted, ref, watch} from "vue";
import {authFetch, readJson} from "@/composables/useAuthFetch";
import useUserSafe from "@/composables/useUserSafe";

export const ATTENDEE_SECTIONS = [
  // sessions: the categories whose filter shows the section too, the event's every delivery
  // (Seán, 2026-11-03: #Masterclass too)
  {kind: "masterclass", label: "Online Masterclass Attendees",
    sessions: ["Masterclass", "Online Masterclass", "Virtual Masterclass", "Online", "Virtual"]},
  {kind: "workshop", label: "Online Workshop Attendees",
    sessions: ["Online Workshop", "Virtual Workshop", "Online", "Virtual"]}
];
const PRESENCE_MS = 2 * 60 * 1000;

export const directories = ref({});
export const presence = ref({});
// why each list the viewer can't see is closed: {kind: {reason: "not-registered" |
// "not-joined" | "not-open", opensAt}} (the server's; signed out: none, the page says to sign in)
export const closedReasons = ref({});
// admins, editors and testers only: each event's registrants who never joined
// ({masterclass: […]})
export const otherRegistrants = ref({});
// the server sent the registrants (admins, editors and testers): the sections' reload button too
export const isStaffView = ref(false);
export const isReloading = ref(false);
const FIRST_YEAR = 2026; // the directories begin (the server's attendees.js)
export const loadedYear = ref(0); // the year whose answer is in: until then, no "why" lines

// the Category menu's one item for them (Seán, 2026-11-03): every section the viewer may see
export const ALL_ATTENDEES = "Attendees";

// an attendee filter: the one item, or a section's own label (its address, e.g. /live's link)
export const getIsAttendeeFilter = (label) => {
  return label === ALL_ATTENDEES || Boolean(getSectionByLabel(label));
};

export const getSectionByLabel = (label) => {
  return ATTENDEE_SECTIONS.find((section) => section.label === label) || null;
};

export const getIsContactHash = (hash) => /^#?contact-attendee&/.test(String(hash || ""));

// "#Online-Masterclass-Attendees" (the page's filter fragment; "-", as "_" means a person), or
// the Contact dialog's "#contact-attendee&…&from=Online-Masterclass-Attendees" → its label, or ""
export function getLabelFromHash(hash) {
  const parts = decodeURIComponent(String(hash || "")).replace(/^#/, "").split("&");
  const from = parts.find((part) => part.startsWith("from="))?.slice(5);
  const text = getIsContactHash(hash) ? from || "" : parts[0];
  return getSectionByLabel(text.replace(/-/g, " "))?.label || "";
}

export function getAttendee(kind, id) {
  return (directories.value[kind] || []).find((row) => row.id === id) || null;
}

async function fetchDirectories(year) {
  const response = await authFetch(`/api/v1/user/attendees/${year}`);
  const data = response.ok ? await readJson(response) : {};
  const {closed = {}, others = {}, ...lists} = data;
  [directories.value, closedReasons.value, loadedYear.value] = [lists, closed, year];
  [otherRegistrants.value, isStaffView.value] = [others, "others" in data];
}

async function fetchPresence(year) {
  if (document.hidden || !Object.keys(directories.value).length) return;
  const response = await authFetch(`/api/v1/user/attendees/${year}/presence`);
  if (response.ok) presence.value = await readJson(response);
}

// staff's reload (People/Attendees.vue): the lists again, as people register and join
export async function reloadAttendees(year) {
  isReloading.value = true;
  try {
    await fetchDirectories(year);
    await fetchPresence(year);
  } finally {
    isReloading.value = false;
  }
}

export default function useAttendees(getYear) {
  const {isSignedIn} = useUserSafe();
  let timer = null;
  const load = async () => {
    if (!isSignedIn.value) {
      return ([directories.value, closedReasons.value, otherRegistrants.value] = [{}, {}, {}]);
    }
    loadedYear.value = 0;
    await fetchDirectories(getYear());
    await fetchPresence(getYear());
  };
  const onVisible = () => !document.hidden && fetchPresence(getYear());
  onMounted(() => {
    timer = setInterval(() => fetchPresence(getYear()), PRESENCE_MS);
    document.addEventListener("visibilitychange", onVisible);
  });
  onUnmounted(() => {
    clearInterval(timer);
    document.removeEventListener("visibilitychange", onVisible);
  });
  watch([isSignedIn, getYear], load, {immediate: true});
  // every section from 2026, for everyone (Seán, 2026-11-03): its list, or a line saying why not
  const sections = computed(() => (getYear() >= FIRST_YEAR ? ATTENDEE_SECTIONS : []));
  return {directories, presence, sections, loadedYear};
}
